import { portalFormat, portalLocale, portalText } from "../i18n/portal.js";
/**
 * Caller protection's notices in the notification centre — plan-029 unit **WAPP-HIST1** (design §40.19.6, §40.25.2,
 * §40.25.6, §40.13.2; D-6, D-12), over API-READ1's derived rows:
 *
 *   `party_protection.party_refused_often`          detail { voice_agent_id, voice_agent_name, last_three_digit, count, day }
 *   `party_access.internal_voice_agent_lock_pending` detail { voice_agent_id, voice_agent_name }
 *   `party_access.internal_voice_agent_locked`       detail { voice_agent_id, voice_agent_name }
 *
 * THE WIRE'S TITLE IS ENGLISH (ADR 0051); the console words each notice in the person's language from its `detail`, the
 * facts API-READ1 added for exactly this. The last three digits are the most a notice ever names — never a number.
 *
 * WHERE IT OPENS. "Refused often" opens the VoiceAgent's History with the "Refused" chip on, so the calls are listed and
 * "Block this number" is one click away; the internal-access notices open its Channels tab, where the list is edited.
 * Built from the detail's VoiceAgent id, not the wire's path, so the chip is always the console's own code.
 *
 * A notice whose detail is malformed (a field missing) is shown as the API worded it, never dropped and never guessed.
 */
import { PARTY_REFUSED_FILTER } from "../api/party-protection-view.js";

export const PARTY_NOTICE_KIND = [
  "party_protection.party_refused_often",
  "party_access.internal_voice_agent_lock_pending",
  "party_access.internal_voice_agent_locked",
] as const;
export type PartyNoticeKind = (typeof PARTY_NOTICE_KIND)[number];

export function isPartyNoticeKind(kind: string): kind is PartyNoticeKind {
  return (PARTY_NOTICE_KIND as readonly string[]).includes(kind);
}

/** The notice in the person's words: title, second line and where it opens. */
export interface PartyNoticeWords {
  readonly title: string;
  readonly dek: string;
  readonly linkPath: string;
}

/** The detail's facts, or `null` when one the words need is missing or malformed. */
interface PartyNoticeFact {
  readonly voiceAgentId: string;
  readonly voiceAgentName: string;
  readonly lastThreeDigit: string | null;
  readonly count: number | null;
  readonly day: string | null;
}

function factOf(detail: unknown): PartyNoticeFact | null {
  if (typeof detail !== "object" || detail === null || Array.isArray(detail)) return null;
  const one = detail as Readonly<Record<string, unknown>>;
  const voiceAgentId = one["voice_agent_id"];
  const voiceAgentName = one["voice_agent_name"];
  if (typeof voiceAgentId !== "string" || !/^[0-9a-f-]{36}$/i.test(voiceAgentId)) return null;
  if (typeof voiceAgentName !== "string" || voiceAgentName.trim() === "") return null;
  const last = one["last_three_digit"];
  const count = one["count"];
  const day = one["day"];
  return {
    voiceAgentId,
    voiceAgentName,
    lastThreeDigit: typeof last === "string" && /^\d{3}$/.test(last) ? last : null,
    count: typeof count === "number" && Number.isInteger(count) && count > 0 ? count : null,
    day: typeof day === "string" && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null,
  };
}

/** Today's calendar day where the console runs, `YYYY-MM-DD`. */
function localDayOf(at: Date): string {
  const two = (value: number): string => String(value).padStart(2, "0");
  return `${String(at.getFullYear())}-${two(at.getMonth() + 1)}-${two(at.getDate())}`;
}

/** "28 October" — a refused-often day that is not today. */
function dayWordsOf(day: string): string {
  return portalFormat(portalLocale()).date(new Date(`${day}T12:00:00`), { day: "numeric", month: "long" });
}

/**
 * The words of one caller-protection notice, or `null` when its detail lacks a fact they need (the API's English row is
 * shown then). `now` decides whether "today" or the day is said.
 */
export function partyNoticeWordsOf(kind: PartyNoticeKind, detail: unknown, now: Date): PartyNoticeWords | null {
  const fact = factOf(detail);
  if (fact === null) return null;
  const voiceAgentPath = `/agents/${fact.voiceAgentId}`;
  if (kind === "party_protection.party_refused_often") {
    if (fact.count === null || fact.day === null) return null;
    const values = { times: fact.count, voice_agent_name: fact.voiceAgentName, day: dayWordsOf(fact.day) };
    const today = fact.day === localDayOf(now);
    const title =
      fact.lastThreeDigit === null
        ? portalText("callerProtection", today ? "notice.refused_often_no_digit" : "notice.refused_often_on_no_digit", values)
        : portalText("callerProtection", today ? "notice.refused_often" : "notice.refused_often_on", {
            ...values,
            last_three: fact.lastThreeDigit,
          });
    return {
      title,
      dek: portalText("callerProtection", "notice.refused_often_dek"),
      linkPath: `${voiceAgentPath}?tab=history&outcome=${PARTY_REFUSED_FILTER}`,
    };
  }
  const locked = kind === "party_access.internal_voice_agent_locked";
  return {
    title: portalText("callerProtection", locked ? "notice.internal_locked" : "notice.internal_lock_pending", {
      voice_agent_name: fact.voiceAgentName,
    }),
    dek: portalText("callerProtection", locked ? "notice.internal_locked_dek" : "notice.internal_lock_pending_dek"),
    linkPath: `${voiceAgentPath}?tab=channels`,
  };
}
